/**
 * Metric Glossary - Single source of truth for metric explanations
 * 
 * Each entry answers:
 * 1. "What is it?"
 * 2. "Why should I care?"
 * 3. (Optional) "How do we calculate it?"
 * 
 * Keep each metric to 6 total lines max.
 */

export const METRIC_GLOSSARY = {
  // ═══════════════════════════════════════════════════════════════
  // REVENUE METRICS
  // ═══════════════════════════════════════════════════════════════
  
  "mrr": {
    title: "Monthly Recurring Revenue (MRR)",
    description: [
      "The total subscription revenue expected each month.",
      "This shows how predictable and stable your income is.",
    ],
    calculation: "Sum of all active subscription monthly prices",
    why_it_matters: "Higher MRR = more predictable cash flow for the business.",
  },

  "arr": {
    title: "Annual Recurring Revenue (ARR)",
    description: [
      "MRR multiplied by 12 — your yearly subscription run rate.",
      "Useful for long-term planning and investor conversations.",
    ],
    calculation: "MRR × 12",
    why_it_matters: "The standard metric investors use to value subscription businesses.",
  },

  "gross_revenue": {
    title: "Gross Revenue",
    description: [
      "Total money collected before any refunds or chargebacks.",
      "The 'top line' number showing raw sales volume.",
    ],
    calculation: "Sum of all payment amounts",
    why_it_matters: "Shows your sales momentum before operational costs.",
  },

  "net_revenue": {
    title: "Net Revenue",
    description: [
      "Gross revenue minus refunds, chargebacks, and disputes.",
      "What you actually keep after customer reversals.",
    ],
    calculation: "Gross − Refunds − Chargebacks",
    why_it_matters: "The real money in your bank account.",
  },

  "refunds": {
    title: "Refunds",
    description: [
      "Money returned to customers who requested their payment back.",
      "High refunds may indicate product issues or billing confusion.",
    ],
    why_it_matters: "Monitor for sudden spikes that signal problems.",
  },

  "chargebacks": {
    title: "Chargebacks / Disputes",
    description: [
      "When a customer disputes a charge with their bank.",
      "These are more serious than refunds — may indicate fraud.",
    ],
    why_it_matters: "Too many chargebacks can get your payment processing shut down.",
  },

  "one_time_sales": {
    title: "One-Time Sales",
    description: [
      "Revenue from single purchases (not subscriptions).",
      "Includes consumables like LoveCoins, boosts, etc.",
    ],
    why_it_matters: "Shows demand for add-on features beyond subscriptions.",
  },

  // ═══════════════════════════════════════════════════════════════
  // USER & ENGAGEMENT METRICS
  // ═══════════════════════════════════════════════════════════════

  "total_users": {
    title: "Total Users",
    description: [
      "All accounts that have completed registration.",
      "Includes both active and dormant users.",
    ],
    why_it_matters: "Your total addressable user base for marketing and features.",
  },

  "active_24h": {
    title: "Active Users (24h)",
    description: [
      "Users who logged in or took any action in the last 24 hours.",
      "A real-time pulse of your platform's activity.",
    ],
    why_it_matters: "Healthy apps have consistent daily active users.",
  },

  "dau": {
    title: "Daily Active Users (DAU)",
    description: [
      "Unique users who engaged with the app today.",
      "The core measure of daily engagement.",
    ],
    calculation: "Count of unique users with activity today",
    why_it_matters: "Growing DAU means people keep coming back.",
  },

  "mau": {
    title: "Monthly Active Users (MAU)",
    description: [
      "Unique users who engaged in the last 30 days.",
      "Shows your monthly reach and engagement breadth.",
    ],
    calculation: "Count of unique users with activity in last 30 days",
    why_it_matters: "DAU/MAU ratio reveals how 'sticky' your app is.",
  },

  "new_users_7d": {
    title: "New Users (7 days)",
    description: [
      "Accounts created in the last 7 days.",
      "Measures your acquisition momentum.",
    ],
    why_it_matters: "Consistent new user growth is essential for long-term health.",
  },

  "conversion_rate": {
    title: "Conversion Rate",
    description: [
      "Percentage of users who completed a desired action.",
      "Usually: free users who upgraded to paid.",
    ],
    calculation: "(Converted ÷ Total Eligible) × 100",
    why_it_matters: "Small conversion improvements = big revenue gains.",
  },

  // ═══════════════════════════════════════════════════════════════
  // SUBSCRIPTION METRICS
  // ═══════════════════════════════════════════════════════════════

  "active_subs": {
    title: "Active Subscriptions",
    description: [
      "Users currently paying for a subscription tier.",
      "Your recurring revenue base.",
    ],
    why_it_matters: "The foundation of predictable monthly income.",
  },

  "elite_members": {
    title: "Elite Members",
    description: [
      "Users on the Elite subscription tier.",
      "Your core paying customer segment.",
    ],
    why_it_matters: "Elite members drive most of the subscription revenue.",
  },

  // ═══════════════════════════════════════════════════════════════
  // SAFETY & MODERATION METRICS
  // ═══════════════════════════════════════════════════════════════

  "open_reports": {
    title: "Open Reports",
    description: [
      "User-submitted reports waiting for review.",
      "Each one is a potential safety or policy issue.",
    ],
    why_it_matters: "Quick response to reports keeps the community safe and trusting.",
  },

  "active_disputes": {
    title: "Active Disputes",
    description: [
      "Payment disputes or chargebacks being contested.",
      "Each requires documentation and response.",
    ],
    why_it_matters: "Unresolved disputes can result in lost funds and higher fees.",
  },

  "guardian_alerts": {
    title: "Guardian Alerts",
    description: [
      "AI-flagged content or behavior for human review.",
      "The automated first line of defense.",
    ],
    why_it_matters: "Catch problems before users report them.",
  },

  "moderation_queue": {
    title: "Moderation Queue",
    description: [
      "Content waiting for human moderator review.",
      "Photos, messages, or profiles flagged for inspection.",
    ],
    why_it_matters: "A growing queue means you may need more moderator capacity.",
  },

  // ═══════════════════════════════════════════════════════════════
  // MATCHING & ACTIVITY METRICS
  // ═══════════════════════════════════════════════════════════════

  "total_matches": {
    title: "Total Matches",
    description: [
      "All-time count of mutual matches between users.",
      "Shows cumulative connection activity.",
    ],
    why_it_matters: "Matches are the core value proposition of the platform.",
  },

  "matches_today": {
    title: "Matches Today",
    description: [
      "New mutual matches created in the last 24 hours.",
      "Real-time indicator of matching activity.",
    ],
    why_it_matters: "Daily match volume shows if the algorithm is working.",
  },

  "lounge_sessions": {
    title: "Lounge Sessions",
    description: [
      "Community room activity sessions today.",
      "Group conversations and events happening now.",
    ],
    why_it_matters: "Active lounges create community beyond 1:1 matching.",
  },

  // ═══════════════════════════════════════════════════════════════
  // IDENTITY & VERIFICATION
  // ═══════════════════════════════════════════════════════════════

  "identity_started": {
    title: "Verification Started",
    description: [
      "Users who began the identity verification flow.",
      "Clicked 'Verify My Identity' but may not have finished.",
    ],
    why_it_matters: "Shows interest in verification before seeing completion rates.",
  },

  "identity_verified": {
    title: "Identity Verified",
    description: [
      "Users who successfully completed ID verification.",
      "Unlocks premium features and builds trust.",
    ],
    calculation: "Completed verifications ÷ Started × 100 = conversion",
    why_it_matters: "Verified users are more engaged and trustworthy.",
  },

  "verification_conversion": {
    title: "Verification Conversion",
    description: [
      "Percentage who finish verification after starting.",
      "Low conversion may indicate friction in the flow.",
    ],
    calculation: "Verified ÷ Started × 100",
    why_it_matters: "If verification kills conversion, you're losing good users.",
  },

  // ═══════════════════════════════════════════════════════════════
  // PAYOUT METRICS
  // ═══════════════════════════════════════════════════════════════

  "mature_payouts": {
    title: "Mature Payouts Pending",
    description: [
      "Earnings that have passed the holding period and are ready to pay.",
      "These need admin action to process.",
    ],
    why_it_matters: "Delays in paying creators hurt trust and retention.",
  },

  "pending_payouts": {
    title: "Pending Payouts",
    description: [
      "Total earnings waiting to be paid out.",
      "Includes both maturing and ready-to-pay amounts.",
    ],
    why_it_matters: "Know your upcoming cash obligations.",
  },
};

// Export a helper to get metric info with fallback
export function getMetricInfo(key) {
  return METRIC_GLOSSARY[key] || {
    title: "About this metric",
    description: ["Definition not added yet."],
  };
}
